import { ReactElement } from 'react';
import styled from "styled-components";
import { useTranslation } from 'react-i18next';
import { IonCheckbox, IonItem, IonRadio, IonRadioGroup } from '@ionic/react';
import { ConsentCheckboxItemType } from '../../../../types/welcome';

interface ConsentCheckboxListProps {
  items: ConsentCheckboxItemType[];
  // itemId -> answer; state lives in the parent (DisplayConsent).
  // REQUIRED items: true = « J'accepte » ticked; false / undefined = not ticked yet.
  // OPTIONAL items: true = « J'accepte », false = « Je refuse »,
  // undefined = not answered yet (blocks « Suivant »).
  values: Record<number, boolean | undefined>;
  onItemChange: (itemId: number, value: boolean | undefined) => void;
}

// Every consent question is the SAME card: the question on top, its answer below.
// - REQUIRED: one « J'accepte » checkbox. Refusing a required consent means not taking
//   part, so no refusal choice is offered (a single radio would be a choice with a
//   missing answer; a box the person ticks is the standard affirmative act).
// - OPTIONAL: an explicit « J'accepte / Je refuse » choice. A refusal must never be
//   implied by an untouched box (it becomes a REFUSED grant server-side when the item is
//   linked to a consent option).
// The answer rows use Ionic's own controls with the label INSIDE them (the questionnaire
// radio markup, AnswerRadio.tsx) and the scoped `.consent-answer` styles in
// theme/radio.scss — never hand-drawn circles around the native control, which rendered
// as a double ring.
const ConsentCheckboxList = ({ items, values, onItemChange }: ConsentCheckboxListProps): ReactElement => {
  const { t } = useTranslation();

  // The API already returns items ordered, but sort defensively.
  const orderedItems = [...items].sort((a, b) => a.order - b.order);

  return (
    <List>
      {orderedItems.map((item) => (
        <Card key={item.id}>
          <Question>
            {item.is_required ? `${item.label} *` : item.label}
            <Mention>
              {item.is_required
                ? t("home_consent_item_required")
                : t("home_consent_item_optional")}
            </Mention>
          </Question>

          {item.is_required ? (
            <div className="consent-answer">
              <IonItem lines="none" mode="md">
                <IonCheckbox
                  labelPlacement="end"
                  justify="start"
                  checked={values[item.id] === true}
                  onIonChange={(e) => onItemChange(item.id, e.detail.checked)}
                >
                  {t("home_consent_item_accept")}
                </IonCheckbox>
              </IonItem>
            </div>
          ) : (
            // No allowEmptySelection: a re-tap never deselects, and a non-boolean value
            // (never expected) maps to "unanswered", never to a refusal.
            <IonRadioGroup
              className="consent-answer"
              value={typeof values[item.id] === 'boolean' ? values[item.id] : undefined}
              onIonChange={(e) =>
                onItemChange(
                  item.id,
                  typeof e.detail.value === 'boolean' ? e.detail.value : undefined
                )
              }
            >
              <IonItem lines="none" mode="md">
                <IonRadio value={true} labelPlacement="end" justify="start">
                  {t("home_consent_item_accept")}
                </IonRadio>
              </IonItem>
              <IonItem lines="none" mode="md">
                <IonRadio value={false} labelPlacement="end" justify="start">
                  {t("home_consent_item_refuse")}
                </IonRadio>
              </IonItem>
            </IonRadioGroup>
          )}
        </Card>
      ))}
    </List>
  );
};

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

// Soft tinted panel (theme.secondary.* = surface — "consent rows", see theme/tokens.ts).
const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 16px 8px;
  border-radius: 15px;
  background-color: ${props => props.theme.secondary.main};
`;

const Question = styled.div`
  font-weight: bold;
`;

const Mention = styled.div`
  font-size: 0.75rem;
  font-weight: normal;
  font-style: italic;
  opacity: 0.7;
`;

export default ConsentCheckboxList;
